import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetStaffAllListQuery } from '@/api/common';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditServiceTerminationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);

		const defaultValues: DefaultValues<any> = {
			sex: null,
		};

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			dateOfTermination: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			contactTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			serviceTerminationOthersSpecify: Yup.string().when(
				'serviceTerminationOthers',
				{
					is: (serviceTerminationOthers: string) => serviceTerminationOthers,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchServiceTerminationOthers = methods.watch(
			'serviceTerminationOthers'
		);

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.numServiceExit')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDatePicker
									control={methods.control}
									name='dateOfTermination'
									label={t('preAdmission.dateOfTermination')}
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								/>
							</Grid>
							<Grid size={12}>
								<Typography
									variant='body2'
									color='common.neutral40'
									sx={{ fontWeight: 'bold' }}
								>
									{t('preAdmission.reasons')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='interventionCompleted'
										label={t('preAdmission.interventionCompleted')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='admittedToResidential'
										label={t('preAdmission.admittedToResidential')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='admittedToPsychiatricHospital'
										label={t('preAdmission.admittedToPsychiatricHospital')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='persistentlyUnmotivated'
										label={t('preAdmission.persistentlyUnmotivated')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='selfWithdrawal'
										label={t('preAdmission.selfWithdrawal')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='untraceable'
										label={t('preAdmission.untraceable')}
									/>
								</Box>

								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='serviceTerminationOthers'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('serviceTerminationOthersSpecify', '');
											methods.clearErrors('serviceTerminationOthersSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchServiceTerminationOthers}
										variantType={'standard'}
										control={methods.control}
										name='serviceTerminationOthersSpecify'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.specify')}${
											watchServiceTerminationOthers ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '0 0 24px' }} />
						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.numRemarks')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									multiline
									name='serviceTerminationRemarks'
									label={t('preAdmission.serviceTerminationRemarks')}
									rows={2}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '0 0 24px' }} />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.shouldEnquiries')}
								</Typography>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='contactWorker'
									label={t('preAdmission.name')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactOfficerInCharge'
									label={t('preAdmission.officerInCharge')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditServiceTerminationForm.displayName = 'EditServiceTerminationForm';

export default EditServiceTerminationForm;
